import { Injectable, OnDestroy } from '@angular/core';
import { ReplaySubject, Subject, Subscription } from 'rxjs';
import { StateSidebarService } from './state-sidebar.service';
import { ResizedEvent } from 'angular-resize-event';
import { TargetAppService } from '../../core/services/parameter';
import { IRawApplication } from 'projects/common/src/app/interfaces/remote-models/raw-application';
import { IRawApplicationChild } from 'projects/common/src/app/interfaces/remote-models/raw-application-child';
import { CacheService } from 'projects/common/src/app/services';

@Injectable({
  providedIn: 'root',
})
export class MenuService implements OnDestroy {
  public menuSource: ReplaySubject<IRawApplicationChild[]>;
  public menuSelectionSource: ReplaySubject<IRawApplicationChild>;
  public menuSelectionToggleSource: Subject<IRawApplicationChild>;
  public detailVisibilitySource: Subject<boolean>;

  private $menuConfiguration: IRawApplicationChild[];
  public get menuConfiguration(): IRawApplicationChild[] {
    return this.$menuConfiguration;
  }
  public set menuConfiguration(value: IRawApplicationChild[]) {
    this.$menuConfiguration = value;
    this.menuSource.next(value);
  }

  private $menuSelection: IRawApplicationChild;
  public get menuSelection(): IRawApplicationChild {
    return this.$menuSelection;
  }
  public set menuSelection(value: IRawApplicationChild) {
    this.$menuSelection = value;
    this.menuSelectionSource.next(value);
  }

  private $menuToggleSelection: IRawApplicationChild;
  public get menuToggleSelection(): IRawApplicationChild {
    return this.$menuToggleSelection;
  }
  public set menuToggleSelection(value: IRawApplicationChild) {
    this.$menuToggleSelection = value;
    this.menuSelectionToggleSource.next(value);
  }

  private resizeSubscription: Subscription;

  constructor(private sidebarService: StateSidebarService, private app: TargetAppService, private cacheService: CacheService) {
    this.menuSource = new ReplaySubject(1);
    this.menuSelectionSource = new ReplaySubject(1);
    this.menuSelectionToggleSource = new Subject();
    this.detailVisibilitySource = new Subject();
    this.resizeSubscription = this.sidebarService.sizeChanged.subscribe((event: ResizedEvent) => {
      this.detailVisibilitySource.next(event.newWidth > 150);
    });
    this.app
      .getObservable()
      .pipe()
      .subscribe((application: IRawApplication) => {
        this.setMenu(application.childs);
      });
  }

  public setMenu(configuration: IRawApplicationChild[]): void {
    this.menuConfiguration = configuration;
  }

  public setSelection(selection: IRawApplicationChild): void {
    this.clearSelection();

    if (selection) {
      const chain = this.findChain(this.menuConfiguration, selection.routerState);

      if (!chain || chain.length === 0) {
        // Si no se encuentra la cadena, activar solo el nodo seleccionado
        selection.isActive = true;
        selection.underline = true;
      } else {
        // Activar todos los nodos de la cadena
        chain.forEach((node) => (node.isActive = true));

        // Marcar solo el último nodo con underline
        chain[chain.length - 1].underline = true;
      }
    }
    // Guardar selección en sesión para mantenerla después de recargar
    this.cacheService.saveSession('selectedMenu', selection);

    // Guardar selección actual en variables del servicio
    this.menuSelection = selection;
    this.menuSelectionSource.next(selection);
    this.menuSource.next(this.menuConfiguration);
  }

  // Funcion para encontrar de forma recursiva la cadena para los routerState que coincida con selection
  private findChain(menuNodes: IRawApplicationChild[], targetRouterState: string): IRawApplication[] | null {
    for (const node of menuNodes) {
      if (String(node.routerState) === String(targetRouterState)) {
        return [node];
      }

      if (node.childs && node.childs.length > 0) {
        const childChain = this.findChain(node.childs, targetRouterState);
        if (childChain) {
          return [node, ...childChain];
        }
      }
    }

    return null;
  }

  public clearSelection(): void {
    const clearNodes = (nodes: IRawApplication[]) => {
      nodes.forEach((node) => {
        node.isActive = false;
        node.underline = false;
        clearNodes(node.childs);
      });
    };

    if (this.menuConfiguration) {
      clearNodes(this.menuConfiguration);
      this.menuSource.next(this.menuConfiguration);
    }
  }

  public setToggleMenu(selection: IRawApplicationChild): void {
    this.menuToggleSelection = selection;
  }

  public ngOnDestroy(): void {
    this.resizeSubscription.unsubscribe();
  }
}
